/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: UnauthorizedPage.tsx
 🗓️ Created: 2025-01-06
 🧩 Description:
    Unauthorized access page displayed when users try to access
    modules they don't have permission to view.

 🔧 Notes:
    - Displays user-friendly unauthorized access message
    - Shows information about required permissions
    - Provides navigation back to dashboard
    - Integrates with i18n for multilingual support

 🔁 Last Updated: 2025-01-06 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../hooks/useI18n';
import { usePermissions } from '../hooks/usePermissions';
import { useModuleNames } from '../hooks/useModuleNames';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AlertTriangle, ArrowLeft, Home, Shield } from 'lucide-react';

interface LocationState {
  from: string;
  requiredModule: string;
  requiredLevel: string;
}

export default function UnauthorizedPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { getModulePermissionLevel } = usePermissions();
  const { getModuleName, isLoading: isLoadingModuleNames } = useModuleNames();
  
  const state = location.state as LocationState;
  const fromPath = state?.from || '/dashboard';
  const requiredModule = state?.requiredModule;
  const requiredLevel = state?.requiredLevel || 'READ';

  // Get current permission level for the module
  const currentLevel = requiredModule ? getModulePermissionLevel(requiredModule) : 'NONE';

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-8 text-center">
        <div className="flex flex-col items-center space-y-6">
          {/* Icon */}
          <div className="w-20 h-20 bg-destructive/10 rounded-full flex items-center justify-center">
            <Shield className="w-10 h-10 text-destructive" />
          </div>

          {/* Title */}
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-foreground">
              Access Denied
            </h1>
            <p className="text-muted-foreground text-lg">
              You don't have permission to access this module
            </p>
          </div>

          {/* Module Information */}
          {requiredModule && (
            <div className="bg-muted/50 rounded-lg p-4 w-full">
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">
                  Module: {isLoadingModuleNames ? 'Loading...' : getModuleName(requiredModule)}
                </h3>
                <div className="flex items-center justify-center space-x-4 text-sm">
                  <div className="flex items-center space-x-1">
                    <span className="text-muted-foreground">Required:</span>
                    <span className="font-medium text-foreground capitalize">
                      {requiredLevel}
                    </span>
                  </div>
                  <div className="w-px h-4 bg-border"></div>
                  <div className="flex items-center space-x-1">
                    <span className="text-muted-foreground">Your Level:</span>
                    <span className={`font-medium capitalize ${
                      currentLevel === 'NONE' ? 'text-destructive' : 
                      currentLevel === 'READ' ? 'text-yellow-600' :
                      currentLevel === 'WRITE' ? 'text-blue-600' :
                      'text-green-600'
                    }`}>
                      {currentLevel}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Message */}
          <div className="space-y-4">
            <p className="text-muted-foreground">
              You don't have the necessary permissions to access this module. 
              Please contact your administrator if you believe this is an error.
            </p>
            
            {currentLevel !== 'NONE' && (
              <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div className="text-left">
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      You have <strong>{currentLevel}</strong> access to this module, 
                      but <strong>{requiredLevel}</strong> access is required.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 w-full max-w-md">
            <Button 
              onClick={handleGoBack}
              variant="outline" 
              className="flex-1"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </Button>
            <Button 
              onClick={handleGoHome}
              className="flex-1"
            >
              <Home className="w-4 h-4 mr-2" />
              Go to Dashboard
            </Button>
          </div>

          {/* Additional Help */}
          <div className="text-xs text-muted-foreground">
            If you need access to this module, please contact your system administrator.
          </div>
        </div>
      </Card>
    </div>
  );
}
